import { stringMatchAll } from "common/utilities/core/string";
import type { SequenceAlign } from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import type { SequenceSelection } from "sequenceAnalyser/utilities/selection/selection";

export type SequencesSearchScope = "all" | "selection";

export interface SequenceSearchMatch {
  sequencePosition: number;
  residueId: string;
  start: number;
  end: number;
}

type SequencesSearchMatches = Record<string, SequenceSearchMatch[]>;

export interface SequencesSearch {
  query: string;
  scope: SequencesSearchScope;
  matches: SequencesSearchMatches;
}

export function searchSequence(
  sequence: SequenceAlign,
  query: string,
  sequencePosition: number,
  positionStart: number | undefined,
  positionEnd: number | undefined
): SequenceSearchMatch[] {
  const residues = sequence.residues.replace(/\s/g, "");
  const matches = stringMatchAll(residues, query);

  return matches
    .filter(
      (match) =>
        (positionStart === undefined && positionStart === undefined) ||
        (positionStart !== undefined &&
          positionEnd !== undefined &&
          match >= positionStart &&
          match + query.length - 1 <= positionEnd)
    )
    .map((match) => ({
      sequencePosition,
      residueId: `seq-${sequence.id}-${match}`,
      start: match,
      end: match + query.length - 1,
    }));
}

export function searchSequences(
  sequences: SequenceAlign[],
  query: string,
  scope: SequencesSearchScope,
  selection: SequenceSelection
): SequencesSearchMatches {
  return sequences.reduce<SequencesSearchMatches>((acc, sequence, i) => {
    if (scope === "all" || selection.ids.includes(sequence.id)) {
      const matches = searchSequence(
        sequence,
        query,
        i,
        scope === "all" ? undefined : selection.positionStart,
        scope === "all" ? undefined : selection.positionEnd
      );
      if (matches.length > 0) {
        acc[sequence.id] = matches;
      }
    }
    return acc;
  }, {});
}
